/**
 * USE_VARIATION_HIERARCHY - Nested Sections Management Hook
 * 
 * SUMMARY:
 * Manages all hierarchical tree operations for complex variations with nested sections.
 * Handles tree building, section expansion/collapse, validation, and totals calculation.
 * 
 * @param {Array} subTaskArray - Flat array of subtasks
 * @returns {Object} Hierarchy state and handlers
 */

import { useState } from 'react';
import { subTaskCalculation } from '../../../../util/calculations/calculate-sub-task';

export const useVariationHierarchy = (subTaskArray, profitMargin, profitMarginType) => {
    // Hierarchy state
    const [expandedSections, setExpandedSections] = useState({});
    const [selectedParent, setSelectedParent] = useState(null);

    /**
     * Build hierarchical tree from flat array
     * 
     * @param {Array} items - Flat subtask array (defaults to subTaskArray from hook)
     * @param {string|null} parentId - Parent ID to filter by
     * @returns {Array} Hierarchical tree structure
     * 
     * @example
     * const tree = buildHierarchy(); // Get root level items from subTaskArray
     * const tree = buildHierarchy(customItems, parentId); // Get from custom array
     */
    const buildHierarchy = (items = subTaskArray, parentId = null) => {
        if (!items) return [];
        return items
            .filter(item => (item.parent_subtask_id || null) === parentId)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(item => ({
                ...item,
                children: item.is_section ? buildHierarchy(items, item.id) : []
            }));
    };

    /**
     * Toggle section expand/collapse state
     * 
     * @param {string} sectionId - Section ID to toggle
     */
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    /**
     * Get all descendants of a section (recursive)
     * 
     * @param {string} parentId - Parent section ID
     * @returns {Array} Array of all descendant items
     * 
     * @example
     * const descendants = getDescendants('section-1'); // Returns all children recursively
     */
    const getDescendants = (parentId) => {
        if (!subTaskArray) return [];
        const children = subTaskArray.filter(item => item.parent_subtask_id === parentId);
        let descendants = [...children];

        children.forEach(child => {
            if (child.is_section) {
                descendants = descendants.concat(getDescendants(child.id));
            }
        });

        return descendants;
    };

    /**
     * Validate if a section or item can be added at a location
     * Enforces hierarchy rules: no mixing at same level
     * 
     * @param {boolean} isSection - Whether adding a section
     * @param {string|null} parentId - Parent ID (null for root)
     * @returns {Object} Validation result { valid, message }
     * 
     * @example
     * const result = validatePlacement(false, null);
     * if (!result.valid) alert(result.message);
     */
    const validatePlacement = (isSection, parentId = null) => {
        if (!subTaskArray) return { valid: true, message: '' };

        const siblings = subTaskArray.filter(item =>
            (item.parent_subtask_id || null) === parentId
        );

        if (siblings.length === 0) {
            return { valid: true, message: '' };
        }

        const hasActualSubtasks = siblings.some(s => !s.is_section);
        const hasSections = siblings.some(s => s.is_section);

        if (isSection && hasActualSubtasks) {
            return {
                valid: false,
                message: 'Cannot add a section when actual items already exist at this level. Remove items first or add within an existing section.'
            };
        }

        if (!isSection && hasSections) {
            return {
                valid: false,
                message: 'Cannot add an item directly when sections exist at this level. Add the item within a section instead.'
            };
        }

        return { valid: true, message: '' };
    };

    /**
     * Get what can be added at a location
     * 
     * @param {string|null} parentId - Parent ID (null for root)
     * @returns {Object} { canAddSection, canAddItem, message }
     * 
     * @example
     * const allowed = getAllowedChildType(null);
     * // Returns: { canAddSection: true, canAddItem: false, message: '...' }
     */
    const getAllowedChildType = (parentId = null) => {
        if (!subTaskArray) return {
            canAddSection: true,
            canAddItem: true,
            message: 'Can add either sections or items'
        };

        const siblings = subTaskArray.filter(item =>
            (item.parent_subtask_id || null) === parentId
        );

        if (siblings.length === 0) {
            return {
                canAddSection: true,
                canAddItem: true,
                message: 'Can add either sections or items'
            };
        }

        const hasActualSubtasks = siblings.some(s => !s.is_section);
        const hasSections = siblings.some(s => s.is_section);

        return {
            canAddSection: !hasActualSubtasks,
            canAddItem: !hasSections,
            message: hasSections
                ? 'Only sections can be added (sections already exist)'
                : 'Only items can be added (items already exist)'
        };
    };

    /**
     * Calculate totals for a section (recursive)
     * Aggregates all descendant actual items (not sections)
     * 
     * @param {string} sectionId - Section ID
     * @returns {Object} Totals object with material, labor, subcontractor, and total
     * 
     * @example
     * const totals = calculateSectionTotals('section-1');
     * // Returns: { material: {...}, labor: {...}, total: 15000, ... }
     */
    const calculateSectionTotals = (sectionId) => {
        const descendants = getDescendants(sectionId);
        const actualSubtasks = descendants.filter(d => !d.is_section);

        const totals = {
            material: { cost: 0, sale: 0, profit: 0 },
            labor: { cost: 0, sale: 0, profit: 0 },
            subcontractor: { cost: 0, sale: 0, profit: 0 },
            total_profit: 0,
            subtotal: 0,
            total: 0,
            forecastHours: 0
        };

        actualSubtasks.forEach(subtask => {
            const calc = subTaskCalculation({
                subtask,
                profitMargin: profitMargin,
                profitMarginType: profitMarginType,
                isForm: true
            });

            totals.material.cost += calc.material.cost;
            totals.material.sale += calc.material.sale;
            totals.material.profit += calc.material.profit;
            totals.labor.cost += calc.labor.cost;
            totals.labor.sale += calc.labor.sale;
            totals.labor.profit += calc.labor.profit;
            totals.subcontractor.cost += calc.subcontractor.cost;
            totals.subcontractor.sale += calc.subcontractor.sale;
            totals.subcontractor.profit += calc.subcontractor.profit;
            totals.forecastHours += (calc.forecastHours || 0);
        });

        totals.total_profit = totals.material.profit + totals.labor.profit + totals.subcontractor.profit;
        totals.subtotal = totals.material.cost + totals.labor.cost + totals.subcontractor.cost;
        totals.total = totals.material.sale + totals.labor.sale + totals.subcontractor.sale;

        return totals;
    };

    /**
     * Get IDs to delete when deleting a section (includes all descendants)
     * 
     * @param {Object} itemToDelete - The item/section being deleted
     * @param {number} subtaskIndex - Index in the array
     * @param {Array} subTaskId - Original ID array
     * @returns {Array} Array of all IDs to delete
     */
    const getIdsToDelete = (itemToDelete, subtaskIndex, subTaskId) => {
        let idsToDelete = subTaskId;

        if (itemToDelete && itemToDelete.is_section) {
            // Get all descendant IDs
            const descendants = getDescendants(itemToDelete.id);
            idsToDelete = [itemToDelete.id, ...descendants.map(d => d.id)];
        }

        return idsToDelete;
    };

    /**
     * Auto-expand parent section when adding item to it
     * 
     * @param {string} parentId - Parent section ID
     */
    const expandParentSection = (parentId) => {
        if (parentId) {
            setExpandedSections(prev => ({ ...prev, [parentId]: true }));
        }
    };

    return {
        // State
        expandedSections,
        setExpandedSections,
        selectedParent,
        setSelectedParent,

        // Tree operations
        buildHierarchy,
        toggleSection,
        getDescendants,

        // Validation
        validatePlacement,
        getAllowedChildType,

        // Calculations
        calculateSectionTotals,

        // Utilities
        getIdsToDelete,
        expandParentSection,
    };
};

